import {
  AnalyticsSpeedingRequester,
  type HeatmapChart,
  type ReportAggregatedBy,
  type SpeedingChartRequest,
  type SpeedingReportRequest,
  type SpeedingRequest,
  type TimeseriesChart,
} from '@cobliteam/api-lib';

import type { DriverSpeedingTableRow, PageType, SpeedingSummary, VehicleSpeedingTableRow } from '@cobliteam/common-types';
import { Environment } from '@cobliteam/react-environments';
import { useMutation, useQuery } from '@tanstack/react-query';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues
const getAnalyticsSpeedingRequester = () =>
  new AnalyticsSpeedingRequester({
    host: env.apiHost,
  });

const QUERY_KEY = 'speeding';
const TIMESERIES_CHART_KEY = 'speeding-timeseries-chart';
const HEATMAP_KEY = 'speeding-heatmap';

const useSpeed = () => {
  const useGetSpeedingSummary = (speedingRequest: SpeedingRequest, aggregatedBy: PageType) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<SpeedingSummary>({
      queryKey: [`${QUERY_KEY}_SUMMARY`, JSON.stringify(speedingRequest), aggregatedBy],
      queryFn: () => getAnalyticsSpeedingRequester().getSpeedingSummary(speedingRequest, aggregatedBy),
      staleTime: 300000,
    });
  };

  const useGetSpeeding = (speedingRequest: SpeedingRequest, aggregatedBy: PageType) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<VehicleSpeedingTableRow[] | DriverSpeedingTableRow[]>({
      queryKey: [`${QUERY_KEY}_TABLE`, JSON.stringify(speedingRequest), aggregatedBy],
      queryFn: () => getAnalyticsSpeedingRequester().getSpeeding(speedingRequest, aggregatedBy),
      staleTime: 300000,
    });
  };

  const useRequestSpeedingReport = useMutation<
    string,
    unknown,
    {
      reportRequest: SpeedingReportRequest;
      aggregatedBy: ReportAggregatedBy;
    }
  >({
    mutationFn: (variables) => getAnalyticsSpeedingRequester().requestSpeedingReport(variables.reportRequest, variables.aggregatedBy),
  });

  const useGetSpeedingTimeseriesChart = (chartRequest: SpeedingChartRequest) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<TimeseriesChart[]>({
      queryKey: [TIMESERIES_CHART_KEY, JSON.stringify(chartRequest)],
      queryFn: () => getAnalyticsSpeedingRequester().getTimeseriesChart(chartRequest),
      staleTime: 300000,
    });
  };

  const useGetSpeedingHeatmapChart = (chartRequest: SpeedingChartRequest) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<HeatmapChart[]>({
      queryKey: [HEATMAP_KEY, JSON.stringify(chartRequest)],
      queryFn: () => getAnalyticsSpeedingRequester().getHeatmapChart(chartRequest),
      staleTime: 300000,
    });
  };

  return {
    getSpeedingSummary: useGetSpeedingSummary,
    getSpeedingRows: useGetSpeeding,
    getSpeedingTimeseriesChart: useGetSpeedingTimeseriesChart,
    getSpeedingHeatmapChart: useGetSpeedingHeatmapChart,
    requestSpeedingReport: useRequestSpeedingReport,
  };
};

export default useSpeed;
